import { useEffect, useRef, useState } from 'react';
import wasmUrl from 'zxing-wasm/reader/zxing_reader.wasm?url';

// Z-002/SHOP-001..013: gemeinsame Scan-Logik für Einkaufsmodus und die
// Positions-EAN-Eingabe (ListDetailPage). Nutzt bevorzugt die native
// BarcodeDetector-API (Chrome/Android, ML Kit); sonst wird per dynamic import
// der Ponyfill `barcode-detector` (zxing-cpp als WebAssembly) nachgeladen, der
// deutlich robuster ist als reine JS-Decoder. Das WASM wird lokal mitgeliefert
// (kein CDN), damit der Scan auch offline in der PWA funktioniert.
//
// Erkennungsqualität: 1080p-Stream, kontinuierlicher Autofokus, optional
// Taschenlampe/Zoom, Prüfziffernvalidierung und Bestätigung über zwei
// aufeinanderfolgende identische Treffer gegen Fehllesungen.
type Detection = { rawValue: string; format: string };
type DetectorLike = { detect(source: CanvasImageSource): Promise<Detection[]> };
type DetectorCtor = new (opts: { formats: string[] }) => DetectorLike;

type ExtCapabilities = MediaTrackCapabilities & {
  torch?: boolean;
  zoom?: { min: number; max: number; step?: number };
  focusMode?: string[];
};
type ExtConstraint = MediaTrackConstraintSet & { torch?: boolean; zoom?: number; focusMode?: string };

const FORMATS = ['ean_13', 'ean_8', 'upc_a', 'upc_e'];
const SCAN_INTERVAL_MS = 80;
const CONFIRMATIONS = 2;

// Prüfziffer (Modulo 10, Gewichte 3/1 von rechts) für EAN-13, UPC-A und EAN-8.
// UPC-E nutzt ein anderes Schema und wird nur auf Ziffern geprüft.
export function isValidBarcode(code: string, format?: string): boolean {
  if (!/^\d+$/.test(code)) return false;
  if (format === 'upc_e') return code.length === 8 || code.length === 6;
  if (![8, 12, 13].includes(code.length)) return false;
  const digits = code.split('').map(Number);
  const check = digits.pop() as number;
  const sum = digits.reverse().reduce((acc, d, i) => acc + d * (i % 2 === 0 ? 3 : 1), 0);
  return (10 - (sum % 10)) % 10 === check;
}

async function loadDetector(): Promise<DetectorCtor> {
  const native = (window as unknown as { BarcodeDetector?: DetectorCtor }).BarcodeDetector;
  if (native) return native;
  const mod = await import('barcode-detector/ponyfill');
  mod.setZXingModuleOverrides({
    locateFile: (path: string, prefix: string) => (path.endsWith('.wasm') ? wasmUrl : prefix + path),
  });
  return mod.BarcodeDetector as unknown as DetectorCtor;
}

export function BarcodeScanner({
  disabled,
  label = '📷 Barcode scannen',
  onDetect,
  onError,
}: {
  disabled?: boolean;
  label?: string;
  onDetect: (ean: string) => void | Promise<void>;
  onError?: (message: string) => void;
}) {
  const [scanning, setScanning] = useState(false);
  const [torchAvailable, setTorchAvailable] = useState(false);
  const [torchOn, setTorchOn] = useState(false);
  const [zoomRange, setZoomRange] = useState<{ min: number; max: number; step: number } | null>(null);
  const [zoom, setZoom] = useState(1);
  const videoRef = useRef<HTMLVideoElement | null>(null);
  const streamRef = useRef<MediaStream | null>(null);

  function stopScan() {
    streamRef.current?.getTracks().forEach((t) => t.stop());
    streamRef.current = null;
    setScanning(false);
    setTorchAvailable(false);
    setTorchOn(false);
    setZoomRange(null);
  }

  useEffect(() => () => stopScan(), []);

  async function applyTrackConstraint(c: ExtConstraint) {
    const track = streamRef.current?.getVideoTracks()[0];
    await track?.applyConstraints({ advanced: [c as MediaTrackConstraintSet] });
  }

  async function toggleTorch() {
    const next = !torchOn;
    try {
      await applyTrackConstraint({ torch: next });
      setTorchOn(next);
    } catch {
      setTorchAvailable(false);
    }
  }

  async function changeZoom(value: number) {
    setZoom(value);
    try {
      await applyTrackConstraint({ zoom: value });
    } catch {
      /* Zoom nicht änderbar – ignorieren */
    }
  }

  async function openCamera(): Promise<MediaStream> {
    const attempts: MediaStreamConstraints[] = [
      { video: { facingMode: { ideal: 'environment' }, width: { ideal: 1920 }, height: { ideal: 1080 } } },
      { video: { facingMode: { ideal: 'environment' } } },
      { video: true },
    ];
    let lastError: unknown;
    for (const constraints of attempts) {
      try {
        return await navigator.mediaDevices.getUserMedia(constraints);
      } catch (e) {
        lastError = e;
        // Berechtigung verweigert / keine Kamera: weitere Versuche sind sinnlos.
        const name = (e as DOMException).name;
        if (name === 'NotAllowedError' || name === 'SecurityError' || name === 'NotFoundError') break;
      }
    }
    throw lastError;
  }

  async function startScan() {
    if (!navigator.mediaDevices?.getUserMedia) {
      onError?.('Kamerazugriff nicht möglich: Der Browser unterstützt keine Kamera (HTTPS erforderlich).');
      return;
    }
    let stream: MediaStream;
    try {
      stream = await openCamera();
    } catch (e) {
      const name = (e as DOMException)?.name ?? 'Fehler';
      onError?.(`Kamerazugriff nicht möglich (${name}). Bitte EAN manuell eingeben.`);
      return;
    }
    streamRef.current = stream;

    // Zusatzfunktionen nur anbieten, wenn Kamera/Browser sie unterstützen.
    const track = stream.getVideoTracks()[0];
    const caps = (track.getCapabilities?.() ?? {}) as ExtCapabilities;
    if (caps.focusMode?.includes('continuous')) {
      await applyTrackConstraint({ focusMode: 'continuous' }).catch(() => undefined);
    }
    setTorchAvailable(!!caps.torch);
    if (caps.zoom && caps.zoom.max > caps.zoom.min) {
      setZoomRange({ min: caps.zoom.min, max: caps.zoom.max, step: caps.zoom.step ?? 0.1 });
      setZoom(caps.zoom.min);
    }
    setScanning(true);
  }

  // Läuft, sobald das <video> gerendert ist: Stream anbinden, abspielen, scannen.
  useEffect(() => {
    const stream = streamRef.current;
    const video = videoRef.current;
    if (!scanning || !stream || !video) return;
    let cancelled = false;

    void (async () => {
      try {
        video.srcObject = stream;
        await video.play();
      } catch {
        if (cancelled) return;
        stopScan();
        onError?.('Kamerabild konnte nicht angezeigt werden. Bitte EAN manuell eingeben.');
        return;
      }

      let detector: DetectorLike;
      try {
        const Detector = await loadDetector();
        detector = new Detector({ formats: FORMATS });
      } catch (e) {
        if (cancelled) return;
        stopScan();
        onError?.(`Barcode-Erkennung konnte nicht geladen werden (${(e as Error)?.message ?? 'Fehler'}). Bitte EAN manuell eingeben.`);
        return;
      }

      let lastCode: string | null = null;
      let hits = 0;
      const loop = async () => {
        if (cancelled || streamRef.current !== stream || !videoRef.current) return;
        try {
          if (video.readyState >= 2) {
            const found = (await detector.detect(video)).find((c) => isValidBarcode(c.rawValue, c.format));
            if (found) {
              hits = found.rawValue === lastCode ? hits + 1 : 1;
              lastCode = found.rawValue;
              if (hits >= CONFIRMATIONS) {
                stopScan();
                navigator.vibrate?.(60);
                await onDetect(found.rawValue);
                return;
              }
            }
          }
        } catch {
          /* Frame konnte nicht analysiert werden, nächsten Frame versuchen */
        }
        if (!cancelled && streamRef.current === stream) setTimeout(() => void loop(), SCAN_INTERVAL_MS);
      };
      void loop();
    })();

    return () => {
      cancelled = true;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [scanning]);

  return !scanning ? (
    <button type="button" className="primary" onClick={startScan} disabled={disabled}>
      {label}
    </button>
  ) : (
    <div className="stack">
      <div style={{ position: 'relative' }}>
        <video ref={videoRef} style={{ width: '100%', borderRadius: 8, display: 'block' }} muted playsInline />
        <div
          aria-hidden
          style={{
            position: 'absolute',
            inset: '25% 10%',
            border: '2px solid rgba(255,255,255,0.85)',
            borderRadius: 8,
            boxShadow: '0 0 0 9999px rgba(0,0,0,0.25)',
            pointerEvents: 'none',
          }}
        />
      </div>
      {zoomRange && (
        <label>
          Zoom
          <input type="range" min={zoomRange.min} max={zoomRange.max} step={zoomRange.step} value={zoom} onChange={(e) => void changeZoom(Number(e.target.value))} />
        </label>
      )}
      {torchAvailable && (
        <button type="button" onClick={() => void toggleTorch()}>
          {torchOn ? '🔦 Licht aus' : '🔦 Licht an'}
        </button>
      )}
      <button type="button" onClick={stopScan}>
        Scan abbrechen
      </button>
    </div>
  );
}
